import React, { useEffect, useState } from 'react'
import { intl } from 'lang/intl'

import { type Track, useTrack } from '../../../webrtc/webRTCRoom'
import { useSelectedAudioOutputDevice } from '../../../Devices/useDevices'
import { getRole } from '../../../global/blocs/roomInfo'
import { logMessage } from '../../../Logging/log'
import { useIsWebinarLiveState } from '../../../firebase/hooks/useWebinarGlobalState'
import { showNotice } from '../../../Layout/Notices/Notices'
import { UserGesturePromise } from '../../../global/utils/UserGesturePromise'
import { useIsLocalFeed } from '../../../Speakers/blocs/useSpeakers'
import { useIsFeedMuted } from '../../../firebase/hooks/useMuteStates'

interface AudioTrackProps {
  track: Track
  participantId?: number
}

const userGesturePromise = new UserGesturePromise((resolve) => {
  showNotice({
    message: intl.formatMessage({
      defaultMessage:
        "Your browser may be blocking audio playback. If you can't hear other speakers, you can attempt grabbing the audio device again.",
    }),
    cta: {
      dismissOnClick: true,
      label: intl.formatMessage({ defaultMessage: 'Retry' }),
      onClick: resolve,
    },
  })
})

const AudioTrack = ({ track, participantId = 0 }: AudioTrackProps) => {
  const audioOutputDeviceId = useSelectedAudioOutputDevice()
  const role = getRole()
  const [trackElementRef, setTrackElementRef] =
    useState<HTMLAudioElement | null>(null)
  useTrack(track, trackElementRef)
  const isLocalFeed = useIsLocalFeed()
  const isFeedMuted = useIsFeedMuted(participantId)

  const isWebinarLive = useIsWebinarLiveState()

  const isMuted =
    (role === 'headless' && !isWebinarLive) ||
    (!isLocalFeed &&
      (role === 'presenter' || role === 'producer') &&
      !audioOutputDeviceId) ||
    isFeedMuted ||
    !track.isEnabled

  useEffect(() => {
    if (audioOutputDeviceId) {
      trackElementRef
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ?.setSinkId?.(audioOutputDeviceId)
        .catch(async (error: unknown) => {
          if (error instanceof Error && error.name === 'NotAllowedError') {
            await userGesturePromise
            try {
              await trackElementRef.setSinkId(audioOutputDeviceId)
            } catch (error2) {
              showNotice({
                message: intl.formatMessage({
                  defaultMessage:
                    'Could not get access to audio output devices.',
                }),
                isClosable: true,
              })
              logMessage('Failed to set audio output device', {
                level: 'warning',
                extra: {
                  deviceId: audioOutputDeviceId,
                  cause: {
                    error1: error,
                    error2,
                  },
                },
              })
            }
          } else {
            showNotice({
              message: intl.formatMessage({
                defaultMessage:
                  'Failed to change audio output device. The selected device may be unavailable or disconnected.',
              }),
              isClosable: true,
            })
            logMessage('Failed to set audio output device', {
              level: 'warning',
              extra: { deviceId: audioOutputDeviceId, cause: error },
            })
          }
        })
    }
  }, [audioOutputDeviceId, trackElementRef])

  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio muted={isMuted} ref={setTrackElementRef} />
}

export { AudioTrack }
